import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AlertTriangle, BarChart3, CalendarDays, ChevronDown, Crosshair, Database, Dices, Flame, GitBranch, GitCompare, LayoutGrid, LogOut,
  Medal, Menu, Presentation, Radio, Settings, Shield, Sparkles, Swords, Target, Trophy, Users, UsersRound, X,
} from "lucide-react";
import { useAuth } from "../context/Auth";
import { isDesktop } from "../lib/desktop";
import { Dropdown } from "./ui";

// Main tabs; a group opens a dropdown of its pages.
const NAV = [
  { label: "Dashboard", to: "/", icon: LayoutGrid },
  { label: "Matches", icon: Swords, items: [
    ["/scrims", "Scrims", Swords], ["/tournaments", "Tournaments", Trophy], ["/replays", "Replays", Database],
  ] },
  { label: "League", icon: UsersRound, items: [
    ["/teams", "Teams", UsersRound], ["/players", "Players", Users], ["/heroes", "Heroes", Sparkles],
    ["/leaderboard", "Leaderboard & Ratings", Medal],
    ["/ranked", "Live Ranked", Trophy],
  ] },
  { label: "Analysis", icon: BarChart3, items: [
    ["/analytics", "Analytics", BarChart3], ["/mistakes", "Mistakes", AlertTriangle], ["/weekly", "Weekly Report", CalendarDays], ["/compare", "Compare", GitCompare],
    ["/heatmaps", "Heatmaps", Flame],
  ] },
  { label: "Prep", icon: Target, items: [
    ["/scouting", "Scouting", Crosshair], ["/prep", "Prep Lane", Target], ["/draft", "Draft Sim", GitBranch],
    ["/simulator", "Win Simulator", Dices], ["/canvas", "Coaching Canvas", Presentation],
  ] },
];

const inGroup = (group, path) => group.items.some(([to]) => path === to || path.startsWith(`${to}/`));
// rivals-stats2.0 nav look: #a0a0a0 idle, white on a green tint with a green rule when active.
const itemClass = ({ isActive }) =>
  `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors border-l-2 ${isActive ? "border-[#27AE60] bg-[#27AE60]/10 text-white" : "border-transparent text-[#a0a0a0] hover:text-white hover:bg-white/5"}`;
const tabClass = (active) =>
  `inline-flex h-16 items-center gap-2 border-b-2 px-4 text-sm font-medium transition-colors ${active ? "border-[#27AE60] bg-[#27AE60]/10 text-white" : "border-transparent text-[#a0a0a0] hover:text-white hover:bg-white/5"}`;

function UserMenu({ user, logout }) {
  return (
    <Dropdown align="right" trigger={({ open, toggle }) => (
      <button onClick={toggle} aria-expanded={open} aria-haspopup="menu" className="flex h-16 items-center gap-2.5 pl-3 text-left">
        <span className="hidden sm:block min-w-0 text-right">
          <span className="block text-sm font-semibold truncate max-w-[160px]">{user?.name}</span>
          <span className="block text-xs text-[#666] truncate max-w-[160px]">{user?.email}</span>
        </span>
        <ChevronDown size={14} className="text-[#666]" />
      </button>
    )}>
      {isDesktop && <NavLink to="/capture" className={itemClass}><Radio size={15} />Capture & uploads</NavLink>}
      <NavLink to="/settings" className={itemClass}><Settings size={15} />Settings</NavLink>
      <button onClick={logout} className="flex w-full items-center gap-3 px-6 py-3 text-sm font-medium text-[#a0a0a0] hover:text-[#ff2a2a] hover:bg-white/5 border-l-2 border-transparent">
        <LogOut size={15} />Logout
      </button>
    </Dropdown>
  );
}

function MobileMenu({ path, onClose }) {
  return (
    <div className="lg:hidden border-t border-white/10 bg-[#0d0d0d] max-h-[calc(100vh-4rem)] overflow-y-auto">
      {NAV.map((group) => (
        <div key={group.label} className="border-b border-white/10 py-2">
          {group.to ? (
            <NavLink to={group.to} end className={itemClass} onClick={onClose}><group.icon size={15} />{group.label}</NavLink>
          ) : (
            <>
              <p className={`px-6 pt-2 pb-1 label ${inGroup(group, path) ? "!text-[#27AE60]" : ""}`}>{group.label}</p>
              {group.items.map(([to, label, Icon]) => (
                <NavLink key={to} to={to} className={itemClass} onClick={onClose}><Icon size={15} />{label}</NavLink>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0d0d0d]">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-2 px-4 sm:px-6">
          <button className="lg:hidden -ml-1 p-2 text-[#a0a0a0] hover:text-white" onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <NavLink to="/" className="flex items-center gap-3 mr-6 shrink-0">
            <span className="w-9 h-9 bg-[#27AE60] flex items-center justify-center shrink-0"><Shield className="w-5 h-5 text-black" /></span>
            <span className="min-w-0">
              <span className="block font-display text-2xl leading-none tracking-tight">Rivals<span className="text-[#27AE60]">STATS</span></span>
              <span className="block label !text-[0.6rem]">Scrim Database</span>
            </span>
          </NavLink>
          <nav className="hidden lg:flex items-center" aria-label="Main">
            {NAV.map((group) => (group.to ? (
              <NavLink key={group.label} to={group.to} end className={({ isActive }) => tabClass(isActive)}>{group.label}</NavLink>
            ) : (
              <Dropdown key={group.label} trigger={({ open, toggle }) => (
                <button onClick={toggle} aria-expanded={open} aria-haspopup="menu" className={tabClass(inGroup(group, pathname) || open)}>
                  {group.label}<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
              )}>
                {group.items.map(([to, label, Icon]) => (
                  <NavLink key={to} to={to} className={itemClass}><Icon size={15} />{label}</NavLink>
                ))}
              </Dropdown>
            )))}
          </nav>
          <div className="ml-auto"><UserMenu user={user} logout={logout} /></div>
        </div>
        {mobileOpen && <MobileMenu path={pathname} onClose={() => setMobileOpen(false)} />}
      </header>
      <div className="page-shell">{children}</div>
    </div>
  );
}
